import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SofmatPackage from "./SofmatPackage";

export default function Billing() {
  const { user } = useAuth();
  return user?.auth_provider === "sofmat_sso" ? <SofmatPackage /> : <LegacyBilling />;
}

function LegacyBilling() {
  const [sub, setSub] = useState(null);
  const [entitlements, setEnt] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: e }, { data: u }, plansResp] = await Promise.all([
      api.get("/billing/subscription"),
      api.get("/billing/entitlements"),
      api.get("/billing/usage"),
      api.get("/plans"),
    ]);
    setSub(s); setEnt(e); setUsage(u);
    setPlans((plansResp.data.plans || []).sort((a, b) => a.display_order - b.display_order));
  };
  useEffect(() => { load(); }, []);

  const changePlan = async (plan_key) => {
    if (!window.confirm(`Cambiare piano a "${plan_key}" (${period})? Nessun addebito reale — MOCK.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/change-plan", { plan_key, period });
      toast.success(`Piano aggiornato: ${plan_key}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!entitlements) return <div className="p-8 text-muted-foreground text-sm">Caricamento…</div>;

  const meterBar = (used, hard, softStops) => {
    const pct = hard ? Math.min(100, (used / hard) * 100) : 0;
    const over = hard && used > hard;
    return (
      <div className="w-full">
        <div className="h-2 bg-muted border border-border relative">
          <div className={`h-full ${over ? "bg-destructive" : pct > 80 ? "bg-warning" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] mt-1 font-mono text-muted-foreground">
          <span>{used} usate</span><span>{hard ?? "∞"} incluse</span>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8" data-testid="billing-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Fatturazione</div>
          <h1 className="text-3xl font-display font-bold mt-1">Piano & utilizzo</h1>
        </div>
        <div className="inline-flex border border-border bg-white p-1">
          <button onClick={() => setPeriod("monthly")}
                  className={`px-3 py-1 text-xs uppercase tracking-widest ${period === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  data-testid="billing-period-monthly">Mensile</button>
          <button onClick={() => setPeriod("annual")}
                  className={`px-3 py-1 text-xs uppercase tracking-widest ${period === "annual" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  data-testid="billing-period-annual">Annuale</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border border-border bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Piano corrente</div>
          <div className="text-2xl font-display font-bold mt-1">{entitlements.plan_public_name || "Nessun piano"}</div>
          <div className="text-xs text-muted-foreground mt-1 font-mono">
            {sub ? `Rinnovo: ${(sub.current_period_end || "").slice(0, 10)}` : "—"}
          </div>
          {sub && (
            <div className="mt-3 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Seat inclusi</span><span className="font-mono">{sub.seats}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Siti</span><span className="font-mono">{sub.sites}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Periodo</span><span className="font-mono">{sub.period}</span></div>
            </div>
          )}
          {sub?.demo && <div className="mt-3 text-[10px] px-2 py-1 border border-warning bg-reliability-yellow">SEED DEMO — MOCK, nessun addebito reale.</div>}
        </div>

        <div className="border border-border bg-white p-5 lg:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Utilizzo mese corrente</div>
          <div className="mt-3 space-y-4">
            {Object.entries(usage?.usage || {}).map(([m, v]) => (
              <div key={m} data-testid={`meter-${m}`}>
                <div className="flex justify-between text-xs">
                  <span className="uppercase tracking-widest text-[10px]">{m.replace(/_/g, " ")}</span>
                </div>
                {meterBar(v, (usage.hard_limits || {})[m], (usage.soft_limits || {})[m])}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Cambio piano (MOCK)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {plans.map(p => (
            <div key={p.plan_key} className={`border ${entitlements.plan_key === p.plan_key ? "border-primary" : "border-border"} bg-white p-4`}>
              <div className="font-display font-bold">{p.public_name}</div>
              <div className="text-xs text-muted-foreground">{period === "annual" ? `€${p.annual_price}/anno` : `€${p.monthly_price}/mese`}</div>
              <Button size="sm" className="mt-3 w-full" variant={entitlements.plan_key === p.plan_key ? "outline" : "default"}
                      disabled={busy || entitlements.plan_key === p.plan_key}
                      onClick={() => changePlan(p.plan_key)}
                      data-testid={`change-plan-${p.plan_key}`}>
                {entitlements.plan_key === p.plan_key ? "Attuale" : "Passa a " + p.public_name}
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 border border-border bg-white">
        <div className="px-4 py-3 border-b border-border font-display font-bold text-sm">Entitlement attivi</div>
        <table className="dense-table w-full">
          <tbody>
            {Object.entries(entitlements.entitlements).map(([k, v]) => (
              <tr key={k}>
                <td className="font-mono text-[11px]">{k}</td>
                <td className="text-right">{v ? <Check size={14} className="text-success inline" /> : <X size={14} className="text-muted-foreground inline" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

